import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { fmtMDT } from "../lib/time";
import { cn } from "../lib/utils";
import {
  Radar, Play, RefreshCw, Trash2, Loader2, CheckCircle2, AlertCircle,
  Globe, Lock, Search, Zap, ChevronRight, ExternalLink, FileDown,
} from "lucide-react";

const TOOL_LABELS = {
  tls:       { label: "TLS / cert",       icon: Lock,   hint: "Certificate expiry + protocol version" },
  headers:   { label: "Security headers", icon: Radar,  hint: "HSTS, CSP, X-Frame-Options, etc." },
  subfinder: { label: "Subdomains",       icon: Search, hint: "Passive subdomain enumeration" },
  nuclei:    { label: "Nuclei signatures",icon: Zap,    hint: "8,000+ CVE / misconfig templates" },
  nmap:      { label: "Nmap port scan",   icon: Zap,    hint: "TCP connect + service-version detection" },
  nikto:     { label: "Nikto (web)",      icon: Radar,  hint: "CGI, backup files, outdated software (~4 min)" },
};

const SEV_STYLE = {
  critical: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  high:     "border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  medium:   "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  low:      "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  info:     "border-border bg-muted text-muted-foreground",
};

const SEV_ORDER = ["critical", "high", "medium", "low", "info"];

function SeverityPill({ sev }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", SEV_STYLE[(sev || "info").toLowerCase()])}>
      {(sev || "info").toUpperCase()}
    </span>
  );
}

function StatusChip({ status }) {
  const map = {
    queued:   { icon: Loader2, cls: "text-muted-foreground", spin: false, label: "Queued" },
    running:  { icon: Loader2, cls: "text-sky-500",          spin: true,  label: "Running" },
    complete: { icon: CheckCircle2, cls: "text-emerald-500", spin: false, label: "Complete" },
    error:    { icon: AlertCircle, cls: "text-rose-500",     spin: false, label: "Error" },
  };
  const m = map[status] || map.queued;
  const Icon = m.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11.5px] font-semibold", m.cls)}>
      <Icon className={cn("w-3.5 h-3.5", m.spin && "animate-spin")} />
      {m.label}
    </span>
  );
}

export default function InHouseScans() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const [scans, setScans] = useState([]);
  const [totals, setTotals] = useState(null);
  const [allowlist, setAllowlist] = useState([]);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [scansRes, allowRes] = await Promise.all([
        api.get("/vulnerability/scans"),
        api.get("/vulnerability/allowlist"),
      ]);
      setScans(scansRes.data.scans || []);
      setTotals(scansRes.data.severity_totals || null);
      setAllowlist(allowRes.data.allowlist || []);
    } catch (e) {
      toast({ title: "Couldn't load scans", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any scan is queued or running
  useEffect(() => {
    const anyBusy = scans.some((s) => s.status === "queued" || s.status === "running");
    if (!anyBusy) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [scans, load]);

  return (
    <section className="rounded-xl border border-border bg-card" data-testid="in-house-scans-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Radar className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="text-[14.5px] font-semibold">In-house scans</h3>
            <p className="text-[11.5px] text-muted-foreground">Nuclei + Subfinder + TLS/headers. Allowlisted targets only.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="scans-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setSchedulesOpen(true)} className="gap-1.5" data-testid="scans-schedules-btn">
              <Radar className="w-4 h-4" /> Schedules
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={() => setLauncherOpen(true)} className="gap-1.5" data-testid="scans-launch-btn">
              <Play className="w-4 h-4" /> Scan now
            </Button>
          )}
        </div>
      </div>

      {/* All-time severity totals */}
      {totals && (
        <div className="grid grid-cols-5 gap-2 p-3 border-b border-border bg-muted/20">
          {SEV_ORDER.map((sev) => (
            <div key={sev} className={cn("rounded-lg border p-2 text-center", SEV_STYLE[sev])}>
              <div className="text-[10.5px] uppercase tracking-wide opacity-70">{sev}</div>
              <div className="text-[20px] font-semibold">{totals[sev] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Scans list */}
      <div className="divide-y divide-border">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-14 bg-muted/20 animate-pulse" />)
        ) : scans.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-muted-foreground">
            No scans yet. {isAdmin ? "Click " : ""}<strong>{isAdmin ? "Scan now" : ""}</strong>{isAdmin ? " to run one." : "Ask an admin to launch a scan."}
          </div>
        ) : scans.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setDetailId(s.id)}
            data-testid={`scan-row-${s.id}`}
            className="w-full text-left grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-muted/40 transition-colors"
          >
            <div className="col-span-4 min-w-0">
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="font-semibold text-[13.5px] truncate">{s.target}</div>
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{s.created_by || "system"}</div>
            </div>
            <div className="col-span-3 text-[11.5px] text-muted-foreground truncate">
              {(s.tools_requested || []).join(" · ")}
            </div>
            <div className="col-span-2"><StatusChip status={s.status} /></div>
            <div className="col-span-2 text-[11.5px] text-muted-foreground">
              {s.findings_total ?? 0} finding{(s.findings_total ?? 0) === 1 ? "" : "s"}
              <div className="text-[10.5px] opacity-70">{fmtMDT(s.finished_at || s.started_at || s.created_at)}</div>
            </div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-muted-foreground inline" /></div>
          </button>
        ))}
      </div>

      <ScanLauncher
        open={launcherOpen}
        onClose={() => setLauncherOpen(false)}
        allowlist={allowlist}
        onLaunched={() => { setLauncherOpen(false); load(); }}
      />
      <SchedulesPanel
        open={schedulesOpen}
        onClose={() => setSchedulesOpen(false)}
        allowlist={allowlist}
      />
      <ScanDetail
        scanId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onDeleted={() => { setDetailId(null); load(); }}
      />
    </section>
  );
}

function SchedulesPanel({ open, onClose, allowlist }) {
  const { toast } = useToast();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState("");
  const [intensity, setIntensity] = useState("balanced");
  const [hourUtc, setHourUtc] = useState(5);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vulnerability/schedules");
      setSchedules(data.schedules || []);
    } catch (e) {
      toast({ title: "Couldn't load schedules", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { if (open) { load(); setTarget(allowlist?.[0] || ""); } }, [open, allowlist, load]);

  const add = async () => {
    if (!target.trim()) return;
    setSaving(true);
    try {
      await api.post("/vulnerability/schedules", { target: target.trim(), intensity, hour_utc: hourUtc, enabled: true });
      toast({ title: "Schedule saved" });
      load();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await api.delete(`/vulnerability/schedules/${id}`);
      load();
    } catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  const toggle = async (s) => {
    try {
      await api.post("/vulnerability/schedules", { ...s, enabled: !s.enabled });
      load();
    } catch (e) { toast({ title: "Update failed", variant: "destructive" }); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="schedules-panel">
        <DialogHeader>
          <DialogTitle>Scheduled scans</DialogTitle>
          <DialogDescription>
            One scan per pinned target, once a day, at the chosen UTC hour. Skipped automatically if the same schedule already ran this hour.
          </DialogDescription>
        </DialogHeader>

        {/* Existing schedules */}
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {loading ? (
            <div className="h-12 rounded-md bg-muted/40 animate-pulse" />
          ) : schedules.length === 0 ? (
            <div className="text-[12.5px] text-muted-foreground py-2 text-center">No schedules yet.</div>
          ) : schedules.map((s) => (
            <div key={s.id} className="rounded-lg border border-border p-2.5 flex items-center gap-2" data-testid={`schedule-${s.target}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{s.target}</div>
                <div className="text-[11px] text-muted-foreground">
                  {s.intensity} · daily at {String(s.hour_utc).padStart(2, "0")}:00 UTC
                  {s.last_run_at && <> · last {new Date(s.last_run_at).toLocaleString()}</>}
                </div>
              </div>
              <label className="text-[11px] inline-flex items-center gap-1">
                <Checkbox checked={s.enabled} onCheckedChange={() => toggle(s)} />
                {s.enabled ? "on" : "off"}
              </label>
              <Button variant="ghost" size="icon" onClick={() => del(s.id)} className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>

        {/* Add new */}
        <div className="rounded-lg border border-border p-3 space-y-2 mt-2">
          <div className="text-[12px] font-semibold">Add a schedule</div>
          <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="iplogin.ca" data-testid="schedule-target-input" />
          <div className="flex flex-wrap gap-1.5">
            {allowlist.map((a) => (
              <button key={a} type="button" onClick={() => setTarget(a)} className="text-[11px] rounded-full border border-border px-2 py-0.5 hover:bg-muted">{a}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Intensity</Label>
              <select className="w-full mt-1 rounded-md border border-border bg-background text-[12.5px] px-2 py-1.5" value={intensity} onChange={(e) => setIntensity(e.target.value)} data-testid="schedule-intensity-select">
                <option value="passive">Passive</option>
                <option value="balanced">Balanced (recommended)</option>
                <option value="deep">Deep</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px]">Hour (UTC)</Label>
              <select className="w-full mt-1 rounded-md border border-border bg-background text-[12.5px] px-2 py-1.5" value={hourUtc} onChange={(e) => setHourUtc(+e.target.value)} data-testid="schedule-hour-select">
                {[...Array(24)].map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
              </select>
            </div>
          </div>
          <Button onClick={add} disabled={saving || !target.trim()} size="sm" className="w-full gap-1.5 mt-1" data-testid="schedule-add-btn">
            <Play className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Add schedule"}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScanLauncher({ open, onClose, allowlist, onLaunched }) {
  const { toast } = useToast();
  const [target, setTarget] = useState("");
  const [intensity, setIntensity] = useState("balanced");
  const [tools, setTools] = useState({ tls: true, headers: true, subfinder: true, nuclei: true, nmap: false, nikto: false });
  const [busy, setBusy] = useState(false);

  const applyPreset = useCallback((preset) => {
    setIntensity(preset);
    if (preset === "passive")  setTools({ tls: true, headers: true, subfinder: false, nuclei: false, nmap: false, nikto: false });
    if (preset === "balanced") setTools({ tls: true, headers: true, subfinder: true,  nuclei: true,  nmap: false, nikto: false });
    if (preset === "deep")     setTools({ tls: true, headers: true, subfinder: true,  nuclei: true,  nmap: true,  nikto: true  });
  }, []);

  useEffect(() => {
    if (open) { setTarget(allowlist?.[0] || ""); applyPreset("balanced"); }
  }, [open, allowlist, applyPreset]);

  const submit = async () => {
    const selected = Object.entries(tools).filter(([, v]) => v).map(([k]) => k);
    if (!target.trim()) { toast({ title: "Enter a target", variant: "destructive" }); return; }
    if (selected.length === 0) { toast({ title: "Pick at least one tool", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/vulnerability/scan", { target: target.trim(), tools: selected, intensity });
      toast({ title: "Scan started", description: `${target} — ${intensity} (${selected.length} tool${selected.length === 1 ? "" : "s"}) running in background.` });
      onLaunched?.();
    } catch (e) {
      toast({ title: "Scan rejected", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="scan-launcher-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Play className="w-4 h-4 text-primary" /> Launch scan</DialogTitle>
          <DialogDescription>
            Scans run against allowlisted domains only: <code>{allowlist.join(", ")}</code> and their subdomains.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="scan-target">Target host</Label>
            <Input id="scan-target" data-testid="scan-target-input" placeholder="iplogin.ca" value={target} onChange={(e) => setTarget(e.target.value)} />
            {allowlist.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {allowlist.map((a) => (
                  <button type="button" key={a} onClick={() => setTarget(a)} className="text-[11px] rounded-full border border-border px-2 py-0.5 hover:bg-muted">{a}</button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Intensity</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "passive",  title: "Passive",  hint: "TLS + headers only. 2 requests total." },
                { key: "balanced", title: "Balanced", hint: "Production-safe. Nuclei with intrusive tags excluded." },
                { key: "deep",     title: "Deep",     hint: "All 6 tools, full Nuclei ruleset. Loud." },
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`intensity-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className={cn(
                    "rounded-lg border p-2 text-left transition",
                    intensity === p.key
                      ? "border-primary/60 bg-primary/10 ring-1 ring-primary/40"
                      : "border-border hover:bg-muted/40",
                  )}
                >
                  <div className="text-[12.5px] font-semibold">{p.title}</div>
                  <div className="text-[10.5px] text-muted-foreground leading-tight mt-0.5">{p.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Tools (auto-selected by intensity)</Label>
            {Object.entries(TOOL_LABELS).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <label key={key} className="flex items-start gap-2 rounded-md border border-border p-2 cursor-pointer hover:bg-muted/40">
                  <Checkbox checked={tools[key]} onCheckedChange={(v) => setTools({ ...tools, [key]: !!v })} data-testid={`tool-checkbox-${key}`} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-primary" /> {meta.label}</div>
                    <div className="text-[11.5px] text-muted-foreground">{meta.hint}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="gap-1.5" data-testid="scan-launch-submit-btn">
            <Play className="w-4 h-4" /> {busy ? "Starting…" : "Start scan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScanDetail({ scanId, open, onClose, onDeleted }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!scanId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vulnerability/scans/${scanId}`);
      setData(data);
    } catch (e) {
      toast({ title: "Couldn't load scan", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [scanId, toast]);

  useEffect(() => { if (open && scanId) load(); }, [open, scanId, load]);
  // Poll every 4s while running
  useEffect(() => {
    const status = data?.scan?.status;
    if (!open || (status !== "queued" && status !== "running")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open, data, load]);

  const del = async () => {
    if (!scanId) return;
    if (!window.confirm("Delete this scan and its findings?")) return;
    try {
      await api.delete(`/vulnerability/scans/${scanId}`);
      toast({ title: "Scan deleted" });
      onDeleted?.();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const scan = data?.scan;
  const findings = data?.findings || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scan-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <Globe className="w-4 h-4 text-primary" />
            <span className="truncate">{scan?.target || "Scan"}</span>
            {scan && <StatusChip status={scan.status} />}
          </DialogTitle>
          <DialogDescription>
            {scan?.tools_requested?.join(" · ")}
            {scan?.duration_seconds != null && <> · finished in {scan.duration_seconds}s</>}
          </DialogDescription>
        </DialogHeader>
        {loading && !data ? (
          <div className="space-y-2 py-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-muted/40 animate-pulse" />)}
          </div>
        ) : !scan ? (
          <div className="py-8 text-center text-[13px] text-muted-foreground">No scan.</div>
        ) : (
          <div className="space-y-4">
            {/* Tool status grid */}
            {scan.tool_status && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(scan.tool_status).map(([k, v]) => {
                  const meta = TOOL_LABELS[k];
                  const Icon = meta?.icon || Radar;
                  return (
                    <div key={k} className="rounded-lg border border-border p-2.5">
                      <div className="flex items-center gap-1.5 text-[12px] font-medium">
                        <Icon className="w-3.5 h-3.5 text-primary" /> {meta?.label || k}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {v === "running" && <Loader2 className="w-3 h-3 inline animate-spin mr-1" />}
                        {v}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Subdomains */}
            {(scan.subdomains || []).length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <div className="text-[12.5px] font-semibold mb-1.5">
                  Subdomains discovered <span className="text-muted-foreground">({scan.subdomains.length})</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {scan.subdomains.slice(0, 40).map((sd) => (
                    <span key={sd} className="text-[11px] rounded-md border border-border bg-muted/40 px-1.5 py-0.5 font-mono">{sd}</span>
                  ))}
                  {scan.subdomains.length > 40 && (
                    <span className="text-[11px] text-muted-foreground italic">+{scan.subdomains.length - 40} more</span>
                  )}
                </div>
              </div>
            )}

            {/* Findings */}
            <div>
              <div className="text-[12.5px] font-semibold mb-1.5">Findings ({findings.length})</div>
              {findings.length === 0 ? (
                <div className="rounded-lg border border-border p-4 text-center text-[12.5px] text-muted-foreground">
                  {scan.status === "complete" ? "🎉 Nothing to report." : "Waiting for tools to finish…"}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {findings.map((f) => (
                    <div key={f.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <SeverityPill sev={f.severity} />
                            <span className="text-[10.5px] rounded border border-border bg-muted/50 px-1.5 py-0.5 font-mono">{f.tool}</span>
                            <span className="text-[13px] font-medium">{f.name}</span>
                          </div>
                          {f.description && <div className="text-[12px] text-muted-foreground mt-1 line-clamp-3">{f.description}</div>}
                          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1.5 font-mono">
                            {f.matched_at || f.host}
                          </div>
                          {f.extracted?.["cve-id"] && (
                            <div className="text-[11px] mt-1 text-rose-600 dark:text-rose-400 font-mono">
                              {(f.extracted["cve-id"] || []).join(" · ")}
                            </div>
                          )}
                        </div>
                        {f.template_url && (
                          <a href={f.template_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline shrink-0" title="Template source">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {scan.error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-[12px] p-2.5">
                {scan.error}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {isAdmin && scan && (
            <Button variant="ghost" onClick={del} className="text-destructive hover:text-destructive gap-1.5 mr-auto" data-testid="scan-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          )}
          {scan && (scan.status === "complete" || (findings || []).length > 0) && (
            <Button
              variant="outline"
              className="gap-1.5"
              data-testid="scan-download-report-btn"
              onClick={async () => {
                try {
                  const { data } = await api.get(`/vulnerability/scans/${scan.id}/report`, { responseType: "blob" });
                  const blob = new Blob([data], { type: "text/html" });
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank", "noopener,noreferrer");
                  // Revoke a moment later so the new tab has time to load.
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } catch (e) {
                  toast({ title: "Report failed", description: e?.response?.data?.detail || "", variant: "destructive" });
                }
              }}
            >
              <FileDown className="w-4 h-4" /> Download report
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
